import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Chip,
    Divider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

// Mercor-style input classes
const inputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& fieldset': {
            borderColor: '#d1d5db',
        },
        '&:hover fieldset': {
            borderColor: '#d1d5db',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#4f46e5',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
    },
};

const FormSection = ({ title, children, sidebarWidth = '200px' }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 5 }, mb: 4 }}>
        <Box sx={{ width: { md: sidebarWidth }, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
            {children}
        </Box>
    </Box>
);

const WorkPreferencesTab = ({ userProfile, userId, authAxios, onUpdate, setError }) => {
    const [savingDomains, setSavingDomains] = useState(false);
    const [savingCompensation, setSavingCompensation] = useState(false);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categories, setCategories] = useState([]);

    const [selectedDomains, setSelectedDomains] = useState([]);
    const [compensation, setCompensation] = useState({
        min_compensation_fulltime: '',
        min_compensation_parttime: '',
    });

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setCategoriesLoading(true);
                const response = await authAxios.get('/categories/');
                const data = response.data;
                const allCategories = data.results || data;
                setCategories(allCategories);
            } catch (error) {
                console.error('Failed to fetch categories:', error);
                if (setError) setError('Failed to load domain categories');
            } finally {
                setCategoriesLoading(false);
            }
        };
        
        fetchCategories();
    }, [authAxios, setError]);

    // Initialize form data from userProfile
    useEffect(() => {
        if (userProfile) {
            setSelectedDomains(userProfile.domain_interests || []);
            setCompensation({
                min_compensation_fulltime: userProfile.min_compensation_fulltime || '',
                min_compensation_parttime: userProfile.min_compensation_parttime || '',
            });
        }
    }, [userProfile]);

    // Toggle domain selection
    const toggleDomain = (domainName) => {
        setSelectedDomains(prev =>
            prev.includes(domainName)
                ? prev.filter(name => name !== domainName)
                : [...prev, domainName]
        );
    };

    // Handle compensation changes
    const handleCompensationChange = (field, value) => {
        // Only allow numbers
        if (value === '' || /^\d+$/.test(value)) {
            setCompensation(prev => ({ ...prev, [field]: value }));
        }
    };

    // Save Domain Interests section handler
    const handleSaveDomains = async () => {
        setSavingDomains(true);
        try {
            await authAxios.patch('/self/', {
                domain_interests: selectedDomains,
            });

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save domain interests:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save domain interests';
            if (setError) setError(errorMsg);
        } finally {
            setSavingDomains(false);
        }
    };

    // Save Compensation section handler
    const handleSaveCompensation = async () => {
        setSavingCompensation(true);
        try {
            await authAxios.patch('/self/', {
                min_compensation_fulltime: compensation.min_compensation_fulltime,
                min_compensation_parttime: compensation.min_compensation_parttime,
            });

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save compensation preferences:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save compensation preferences';
            if (setError) setError(errorMsg);
        } finally {
            setSavingCompensation(false);
        }
    };

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
            {/* Domain Interests Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Domain Interests
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Select the areas you're interested in working on
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Interests">
                    {categoriesLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                            <CircularProgress size={20} sx={{ color: '#4f46e5' }} />
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                Loading categories...
                            </Typography>
                        </Box>
                    ) : categories.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                            No categories available
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                            {categories.map((category) => {
                                const isSelected = selectedDomains.includes(category.name);
                                return (
                                    <Chip
                                        key={category.id}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <span>{category.name}</span>
                                                {isSelected && <CheckIcon sx={{ fontSize: 16, ml: 0.5 }} />}
                                            </Box>
                                        }
                                        onClick={() => toggleDomain(category.name)}
                                        sx={{
                                            px: 0.5,
                                            py: 2.5,
                                            borderRadius: '8px',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            border: '1px solid',
                                            borderColor: isSelected ? '#4f46e5' : '#e5e7eb',
                                            bgcolor: isSelected ? '#eef2ff' : 'white',
                                            color: isSelected ? '#4f46e5' : '#374151',
                                            '&:hover': {
                                                borderColor: '#4f46e5',
                                                bgcolor: isSelected ? '#e0e7ff' : '#f9fafb',
                                            },
                                            '& .MuiChip-label': {
                                                px: 1,
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    )}
                </FormSection>

                {/* Save Button for Domain Interests */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveDomains}
                        disabled={savingDomains || categoriesLoading}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {savingDomains ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {/* Minimum Compensation Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Minimum Compensation
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                    Set your minimum expected compensation
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Full-time ($/year)">
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Ex: 120000"
                            value={compensation.min_compensation_fulltime}
                            onChange={(e) => handleCompensationChange('min_compensation_fulltime', e.target.value)}
                            sx={inputStyles}
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ color: '#6b7280', mr: 0.5 }}>$</Typography>
                                ),
                            }}
                        />
                        <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5, fontSize: '0.75rem' }}>
                            Annual salary in USD
                        </Typography>
                    </Box>
                </FormSection>

                <FormSection title="Part-time ($/hour)">
                    <Box sx={{ position: 'relative' }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Ex: 75"
                            value={compensation.min_compensation_parttime}
                            onChange={(e) => handleCompensationChange('min_compensation_parttime', e.target.value)}
                            sx={inputStyles}
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ color: '#6b7280', mr: 0.5 }}>$</Typography>
                                ),
                            }}
                        />
                        <Typography variant="body2" sx={{ color: '#9ca3af', mt: 0.5, fontSize: '0.75rem' }}>
                            Hourly rate in USD
                        </Typography>
                    </Box>
                </FormSection>

                {/* Save Button for Compensation */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveCompensation}
                        disabled={savingCompensation}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {savingCompensation ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default WorkPreferencesTab;
